import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  TextBlock,
  WordDisplayBlock,
  RecallInputBlock,
  StoryBuilderBlock,
  VisualizationBlock,
  MemoryChallengeBlock,
  ReflectionBlock,
  ResultBlock,
  SegmentIntroBlock,
  SegmentSummaryBlock
} from '@/components/SessionBlocks';
import SessionFeedback from '@/components/SessionFeedback';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SessionPlayer() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isPro = user?.subscription?.plan === 'pro' || user?.subscription?.plan === 'enterprise';
  const userAge = typeof user?.consent?.age === 'number' ? user.consent.age : null;

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adaptiveInfo, setAdaptiveInfo] = useState(null);

  const [phaseIndex, setPhaseIndex] = useState(0);
  const [blockIndex, setBlockIndex] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const timerRef = useRef(null);
  // Mirror timeElapsed into a ref so submitAttempt doesn't depend on it
  // (depending on timeElapsed rebuilds submitAttempt every second, which
  // ripples down and resets in-block timers — see WordDisplayBlock.)
  const timeElapsedRef = useRef(0);
  useEffect(() => { timeElapsedRef.current = timeElapsed; }, [timeElapsed]);
  const [scores, setScores] = useState([]);
  const [phaseScores, setPhaseScores] = useState([]);
  const [started, setStarted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Real-time adaptation state
  const [recentScores, setRecentScores] = useState([]);
  const [adaptationApplied, setAdaptationApplied] = useState(false);

  // Load adaptive content — wait for auth to settle first so we don't race
  // the cookie exchange. If any 401 happens, we show a friendly "sign in
  // again" screen instead of auto-redirecting (which causes iOS Safari ITP
  // loops when the cookie gets dropped between requests).
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setError('__auth__');
      setLoading(false);
      return;
    }
    axios.get(`${API}/sessions/${sessionId}/adaptive-content`, { withCredentials: true })
      .then(res => {
        setContent(res.data);
        if (res.data.adaptive) setAdaptiveInfo(res.data.adaptive);
      })
      .catch(err => {
        if (err.response?.status === 401) {
          setError('__auth__');
          return;
        }
        // Fallback to regular content
        axios.get(`${API}/sessions/${sessionId}/content`, { withCredentials: true })
          .then(res => setContent(res.data))
          .catch(e => {
            if (e.response?.status === 401) {
              setError('__auth__');
              return;
            }
            setError(e.response?.data?.detail || 'Failed to load session');
          });
      })
      .finally(() => setLoading(false));
  }, [sessionId, authLoading, user]);

  // Timer
  useEffect(() => {
    if (!started) return;
    timerRef.current = setInterval(() => setTimeElapsed(prev => prev + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [started]);

  const formatTime = (seconds) => {
    const r = Math.max(3600 - seconds, 0);
    return `${String(Math.floor(r / 60)).padStart(2, '0')}:${String(r % 60).padStart(2, '0')}`;
  };

  const phases = content?.phases || [];
  const currentPhase = phases[phaseIndex];
  const blocks = currentPhase?.blocks || [];
  const currentBlock = blocks[blockIndex];
  const totalBlocks = phases.reduce((s, p) => s + (p.blocks?.length || 0), 0);
  const completedBlocks = phases.slice(0, phaseIndex).reduce((s, p) => s + (p.blocks?.length || 0), 0) + blockIndex;
  const progress = totalBlocks > 0 ? (completedBlocks / totalBlocks) * 100 : 0;

  // Real-time adaptation: adjust remaining blocks if struggling/excelling
  const applyRealTimeAdaptation = useCallback((newScores) => {
    if (newScores.length < 2 || adaptationApplied) return;
    const lastTwo = newScores.slice(-2);
    const avgRecent = lastTwo.reduce((s, sc) => s + (sc.accuracy || 0), 0) / 2;

    if (avgRecent < 40) {
      // Struggling: increase time limits and display times for remaining blocks
      setContent(prev => {
        if (!prev) return prev;
        const updated = { ...prev, phases: prev.phases.map((p, pi) => {
          if (pi < phaseIndex) return p;
          return { ...p, blocks: p.blocks.map((b, bi) => {
            if (pi === phaseIndex && bi <= blockIndex) return b;
            const c = { ...b.content };
            if (b.type === 'wordDisplay' && c.display_time) c.display_time = Math.round(c.display_time * 1.3);
            if (b.type === 'recallInput' && c.time_limit) c.time_limit = Math.round(c.time_limit * 1.3);
            if (b.type === 'visualization' && c.duration) c.duration = Math.round(c.duration * 1.2);
            return { ...b, content: c };
          })};
        })};
        updated.adaptive = { ...(updated.adaptive || {}), realtime_adjustment: 'easier' };
        return updated;
      });
      setAdaptationApplied(true);
      toast('Difficulty adjusted to help you improve', { icon: '🧠' });
    } else if (avgRecent > 85) {
      // Excelling: inject a bonus challenge if not already done
      setContent(prev => {
        if (!prev) return prev;
        const currentPhaseData = prev.phases[phaseIndex];
        if (!currentPhaseData || currentPhaseData.blocks.some(b => b.block_id === 'realtime_bonus')) return prev;

        const bonusBlock = {
          block_id: 'realtime_bonus',
          type: 'text',
          content: {
            title: 'Bonus: You\'re On Fire!',
            body: 'Your accuracy is exceptional. The system has increased the challenge for remaining exercises. Push your limits!'
          }
        };

        const updated = { ...prev, phases: prev.phases.map((p, pi) => {
          if (pi !== phaseIndex) return p;
          const newBlocks = [...p.blocks];
          newBlocks.splice(blockIndex + 2, 0, bonusBlock);
          return { ...p, blocks: newBlocks };
        })};
        updated.adaptive = { ...(updated.adaptive || {}), realtime_adjustment: 'harder' };
        return updated;
      });
      setAdaptationApplied(true);
      toast('Challenge increased — you\'re excelling!', { icon: '⚡' });
    }
  }, [phaseIndex, blockIndex, adaptationApplied]);

  const handleScore = useCallback((scoreData) => {
    setScores(prev => [...prev, scoreData]);
    setPhaseScores(prev => [...prev, scoreData]);
    setRecentScores(prev => {
      const updated = [...prev, scoreData];
      applyRealTimeAdaptation(updated);
      return updated;
    });
  }, [applyRealTimeAdaptation]);

  const submitAttempt = useCallback(async () => {
    clearInterval(timerRef.current);
    const totalCorrect = scores.reduce((s, sc) => s + (sc.correct || 0), 0);
    const totalItems = scores.reduce((s, sc) => s + (sc.total || 0), 0);
    const avgAccuracy = scores.length > 0
      ? Math.round(scores.reduce((s, sc) => s + (sc.accuracy || 0), 0) / scores.length)
      : 0;
    const score = totalItems > 0 ? Math.round((totalCorrect / totalItems) * 100) : 0;

    try {
      await axios.post(`${API}/attempts`, {
        session_id: sessionId,
        score,
        accuracy: avgAccuracy,
        answers: { scores },
        time_spent: timeElapsedRef.current,
        difficulty_level: adaptiveInfo?.difficulty_level || 'medium'
      }, { withCredentials: true });
      setShowFeedback(true);
    } catch {
      toast.error('Failed to save results');
      navigate('/dashboard');
    }
  }, [scores, sessionId, navigate, adaptiveInfo]);

  const handleBlockComplete = useCallback(() => {
    if (blockIndex < blocks.length - 1) {
      setBlockIndex(prev => prev + 1);
    } else if (phaseIndex < phases.length - 1) {
      setPhaseIndex(prev => prev + 1);
      setBlockIndex(0);
      setPhaseScores([]);
    } else {
      submitAttempt();
    }
  }, [blockIndex, blocks.length, phaseIndex, phases.length, submitAttempt]);

  const renderBlock = () => {
    if (!currentBlock) return null;
    const isFinalBlock = phaseIndex === phases.length - 1 && blockIndex === blocks.length - 1;

    switch (currentBlock.type) {
      case 'text':
        return <TextBlock content={currentBlock.content} onComplete={handleBlockComplete} />;
      case 'wordDisplay':
        return <WordDisplayBlock content={currentBlock.content} onComplete={handleBlockComplete} />;
      case 'recallInput':
        return <RecallInputBlock content={currentBlock.content} onComplete={handleBlockComplete} onScore={handleScore} userAge={userAge} />;
      case 'storyBuilder':
        return <StoryBuilderBlock content={currentBlock.content} onComplete={handleBlockComplete} />;
      case 'visualization':
        return <VisualizationBlock content={currentBlock.content} onComplete={handleBlockComplete} />;
      case 'memoryChallenge':
        return <MemoryChallengeBlock content={currentBlock.content} onComplete={handleBlockComplete} onScore={handleScore} />;
      case 'reflection':
        return <ReflectionBlock content={currentBlock.content} onComplete={handleBlockComplete} />;
      case 'segmentIntro':
        return <SegmentIntroBlock content={currentBlock.content} onComplete={handleBlockComplete} />;
      case 'segmentSummary':
        return <SegmentSummaryBlock content={currentBlock.content} onComplete={handleBlockComplete} />;
      case 'result':
        return <ResultBlock content={currentBlock.content} scores={currentBlock.content.type === 'final' ? scores : phaseScores} onComplete={handleBlockComplete} isFinal={isFinalBlock} />;
      default:
        return (
          <div className="text-center">
            <p className="text-zinc-500">Unknown block type: {currentBlock.type}</p>
            <button onClick={handleBlockComplete} className="btn-primary mt-4">Skip</button>
          </div>
        );
    }
  };

  // Show feedback overlay after session
  if (showFeedback) {
    return <SessionFeedback sessionId={sessionId} scores={scores} timeElapsed={timeElapsed} />;
  }

  if (authLoading || (loading && !error)) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="session-loading">
        <div className="mono-data text-lg animate-pulse-red">Loading Adaptive Session...</div>
      </div>
    );
  }

  if (error === '__auth__') {
    const redirectUrl = window.location.origin + window.location.pathname;
    const loginUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="session-auth-error">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Sign in to start this session
          </h2>
          <p className="text-zinc-600 mb-2">
            Your session expired or your browser blocked the sign-in cookie.
          </p>
          <p className="text-xs text-zinc-500 mb-8">
            If you're on iPhone and this keeps happening, try Chrome or turn off Private Browsing in Safari.
          </p>
          <a href={loginUrl} className="btn-primary inline-flex items-center gap-3" data-testid="session-signin-again-btn">
            Sign in again
          </a>
          <div className="mt-4">
            <button onClick={() => navigate('/')} className="text-sm text-zinc-500 underline" data-testid="session-go-home-btn">
              Or go to homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="session-error">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Cannot Start Session</h2>
          <p className="text-zinc-600 mb-8">{error}</p>
          <button onClick={() => navigate(-1)} className="btn-primary" data-testid="session-error-back">Go Back</button>
        </div>
      </div>
    );
  }

  if (!started) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="session-start-screen">
        <div className="text-center max-w-md">
          <div className="mono-data text-[#09090B] text-6xl font-black mb-6" data-testid="session-timer-preview">60:00</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="session-title">
            {content?.title}
          </h1>
          <p className="text-zinc-600 mb-2">{phases.length} phases &middot; {totalBlocks} blocks</p>

          {/* Adaptive indicator */}
          {adaptiveInfo?.adapted && (
            <div className="flex items-center justify-center gap-2 my-4 py-2 px-4 border border-[#09090B] inline-flex" data-testid="adaptive-mode-indicator">
              <Zap className="w-4 h-4 text-[#09090B]" />
              <span className="mono-data text-xs text-[#09090B]">
                Adaptive Mode: {adaptiveInfo.difficulty_level?.toUpperCase()}
              </span>
            </div>
          )}

          <p className="text-sm text-zinc-400 mb-10">
            {adaptiveInfo?.adapted
              ? 'This session has been personalized based on your cognitive profile.'
              : 'This session runs for up to 1-hours. Stay focused.'}
          </p>
          <button onClick={() => setStarted(true)} className="btn-accent text-lg px-12 py-5" data-testid="start-session-btn">
            Begin Session
          </button>

          {/* Regenerate button for Pro users */}
          {isPro && (
            <div className="mt-6">
              <button
                onClick={async () => {
                  setRegenerating(true);
                  try {
                    await axios.post(`${API}/sessions/${sessionId}/regenerate`, {}, { withCredentials: true });
                    toast.success('Fresh exercises generated!');
                    const res = await axios.get(`${API}/sessions/${sessionId}/adaptive-content`, { withCredentials: true });
                    setContent(res.data);
                    if (res.data.adaptive) setAdaptiveInfo(res.data.adaptive);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Regeneration failed');
                  } finally { setRegenerating(false); }
                }}
                disabled={regenerating}
                className="mono-data text-xs text-zinc-500 hover:text-[#09090B] flex items-center gap-2 mx-auto transition-colors disabled:opacity-40"
                data-testid="regenerate-btn"
              >
                <RefreshCw className={`w-3 h-3 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Generating fresh exercises...' : 'Regenerate exercises'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="session-player">
      {/* Top Bar */}
      <div className="sticky top-14 z-40 bg-white border-b border-[#09090B]">
        <div className="h-1 bg-zinc-200">
          <div className="h-full bg-[#09090B] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} data-testid="session-progress-bar" />
        </div>
        <div className="container-swiss flex items-center justify-between h-12">
          <div className="flex items-center gap-4">
            <span className="mono-data text-xs text-zinc-500">Phase {phaseIndex + 1}/{phases.length}</span>
            <span className="mono-data text-xs text-zinc-400">{currentPhase?.title}</span>
            {adaptiveInfo?.adapted && (
              <span className="flex items-center gap-1 mono-data text-xs text-[#09090B]" data-testid="adaptive-badge">
                <Zap className="w-3 h-3" /> {adaptiveInfo.difficulty_level}
              </span>
            )}
          </div>
          <div className="timer-display" data-testid="session-timer">{formatTime(timeElapsed)}</div>
        </div>
      </div>

      {/* Block Content */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-12">
        <div className="w-full max-w-4xl" key={`${phaseIndex}-${blockIndex}`}>
          {renderBlock()}
        </div>
      </div>

      {/* Bottom Info */}
      <div className="border-t border-zinc-200 bg-zinc-50">
        <div className="container-swiss flex items-center justify-between h-10">
          <span className="mono-data text-xs text-zinc-400">Block {completedBlocks + 1} of {totalBlocks}</span>
          <span className="mono-data text-xs text-zinc-400">{content?.title}</span>
        </div>
      </div>
    </div>
  );
}
